import { generatePath, Outlet } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { INTEGRATION_BUILDER_KEYS } from 'api/integrationBuilder/constants';
import { IAdapter } from 'api/integrationBuilder/types';
import { ICONS_MAP } from 'constants/icons';
import { PERMISSION_TYPE } from 'hooks/useRBAC';
import { queryClient } from 'queries';
import { normalizeQueryKey } from 'queries/utils';
import { ROUTES } from 'routes/config/constants';
import { IRoutesConfigItem } from 'routes/config/index';
import { appLazy } from 'routes/ErrorBoundary/appReloadLazy';

const IntegrationBuilder = appLazy(
  () => import('routes/pages/AuthorizedStack/IntegrationBuilder'),
);

const Adapter = appLazy(
  () => import('routes/pages/AuthorizedStack/IntegrationBuilder/Adapter/View'),
);
const CreateOperation = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/IntegrationBuilder/Adapter/View/Operation/Create'
    ),
);
const EditOperation = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/IntegrationBuilder/Adapter/View/Operation/Edit'),
);

const ResultCodes = appLazy(
  () => import('routes/pages/AuthorizedStack/Settings/ResultCodes'),
);
const BulkCreateResultCodes = appLazy(
  () => import('routes/pages/AuthorizedStack/Settings/ResultCodes/BulkCreate'),
);

const AuditLogs = appLazy(() => import('routes/pages/AuthorizedStack/AuditLogs'));

export const INTEGRATIONS_CHILDREN: IRoutesConfigItem[] = [
  {
    breadcrumb: 'integrationBuilder.title',
    children: [
      {
        component: IntegrationBuilder,
        path: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.PATH,
        permissions: [PERMISSIONS.VIEW_ADAPTERS_CODES],
      },
      {
        breadcrumb: 'integrationBuilder.title',
        component: AuditLogs,
        path: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.AUDIT_LOGS.PATH,
        permissions: [PERMISSIONS.VIEW_ADAPTERS_CODES, PERMISSIONS.VIEW_AUDIT_LOGS],
        url: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.AUDIT_LOGS.PATH,
      },
      {
        breadcrumb: (params) => {
          if (!params?.code) return '';

          const adapter = queryClient.getQueryData<IAdapter>(
            normalizeQueryKey([INTEGRATION_BUILDER_KEYS.ADAPTER, params.id]),
          );

          return adapter
            ? adapter.code
            : params?.code || 'integrationBuilder.adapter.title';
        },
        children: [
          {
            component: Adapter,
            path: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.ADAPTER
              .SUB_PATH.VIEW.PATH,
            permissions: [
              PERMISSIONS.VIEW_ADAPTERS_CODES,
              PERMISSIONS.CAN_VIEW_DEFINITIONS,
            ],
            permissionType: PERMISSION_TYPE.OR,
          },
          {
            component: CreateOperation,
            path: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.ADAPTER
              .SUB_PATH.VIEW.SUB_PATH.OPERATION.CREATE.PATH,
            permissions: [PERMISSIONS.CAN_CREATE_DEFINITIONS],
          },
          {
            breadcrumb: 'integrationBuilder.operation.edit',
            component: EditOperation,
            path: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.ADAPTER
              .SUB_PATH.VIEW.SUB_PATH.OPERATION.EDIT.PATH,
            permissions: [PERMISSIONS.CAN_UPDATE_DEFINITIONS],
            url: (params) => {
              const id = params?.id;
              const code = params?.code;
              const { PARAMS, PATH } =
                ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.ADAPTER.SUB_PATH
                  .VIEW.SUB_PATH.OPERATION.EDIT;
              return !id || !code
                ? ''
                : generatePath(PATH, { [PARAMS.CODE]: code, [PARAMS.ID]: id });
            },
          },
        ],
        component: Outlet,
        path: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.ADAPTER.SUB_PATH
          .VIEW.PATH,
        url: (params) => {
          const code = params?.id;
          const { PARAMS, PATH } =
            ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.SUB_PATH.ADAPTER.SUB_PATH
              .VIEW;
          return !code ? '' : generatePath(PATH, { [PARAMS.CODE]: code });
        },
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.IntegrationBuilder />,
    path: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.PATH,
    permissions: [PERMISSIONS.VIEW_ADAPTERS_CODES],
    testId: 'navbarIntegrationBuilder',
    url: ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.PATH,
  },
  {
    breadcrumb: 'resultCodes.title',
    children: [
      {
        component: ResultCodes,
        path: ROUTES.INTEGRATIONS.SUB_PATH.RESULT_CODES.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_RESULT_CODES],
      },
      {
        breadcrumb: 'resultCodes.bulkCreate.title',
        component: BulkCreateResultCodes,
        path: ROUTES.INTEGRATIONS.SUB_PATH.RESULT_CODES.SUB_PATH.BULK_CREATE,
        permissions: [PERMISSIONS.CAN_BULK_CREATE_RESULT_CODES],
        url: ROUTES.INTEGRATIONS.SUB_PATH.RESULT_CODES.SUB_PATH.BULK_CREATE,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.ResultCodes />,
    path: ROUTES.INTEGRATIONS.SUB_PATH.RESULT_CODES.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_RESULT_CODES],
    testId: 'navbarResultCodes',
    url: ROUTES.INTEGRATIONS.SUB_PATH.RESULT_CODES.PATH,
  },
];
